import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import type { LocationMapItem, EntryExitItem, ObstacleItem, MapSelectedItem, BoundsRect, AisleSide } from './mapTypes';
import { ENTRY_EXIT_GRID_SIZE, clampPositionToBounds, clampSizeToBounds, rectFromLocation, findParentLocation, platformBoundsForAisle } from './mapTypes';
import { useTranslation } from 'react-i18next';

interface MapPropertiesPanelProps {
  selected: MapSelectedItem;
  locations: LocationMapItem[];
  warehouseWidth: number;
  warehouseHeight: number;
  occupancy?: {
    occupancyRatePercent: number;
    occupiedLocations: number;
    totalPickableLocations: number;
    emptyLocations: number;
  } | null;
  /** Stok bulunan konum id'leri (raf-seviyesi rafların doluluğunu göstermek için) */
  occupiedLocationIds?: Set<string>;
  onWarehouseSizeChange: (w: number, h: number) => void;
  onLocationChange: (id: string, changes: Partial<LocationMapItem>) => void;
  onLocationDelete: (id: string) => void;
  onEntryExitChange: (id: string, changes: Partial<EntryExitItem>) => void;
  onEntryExitDelete: (id: string) => void;
  onObstacleChange: (id: string, changes: Partial<ObstacleItem>) => void;
  onObstacleDelete: (id: string) => void;
  onDuplicate?: () => void;
}

const MapPropertiesPanel: React.FC<MapPropertiesPanelProps> = ({
  selected, locations, warehouseWidth, warehouseHeight, occupancy, occupiedLocationIds,
  onWarehouseSizeChange, onLocationChange, onLocationDelete,
  onEntryExitChange, onEntryExitDelete, onObstacleChange, onObstacleDelete,
  onDuplicate,
}) => {
  const { t } = useTranslation('warehouse');
  const TYPE_LABELS: Record<string, string> = {
    Zone: t('lookup.locationType.Zone', { defaultValue: 'Zone' }),
    Aisle: t('lookup.locationType.Aisle', { defaultValue: 'Aisle' }),
    Platform: t('lookup.locationType.Platform', { defaultValue: 'Platform' }),
    Rack: t('lookup.locationType.Rack', { defaultValue: 'Rack' }),
    Level: t('lookup.locationType.Level', { defaultValue: 'Level' }),
    Bin: t('lookup.locationType.Bin', { defaultValue: 'Bin' }),
    Entry: t('components.mapPropertiesPanel.entry'),
    Exit: t('components.mapPropertiesPanel.exit'),
    Wall: t('components.mapPropertiesPanel.wall'),
    Column: t('components.mapPropertiesPanel.column'),
    FireExit: t('components.mapPropertiesPanel.fireExit'),
    Other: t('components.mapPropertiesPanel.other'),
  };
  // No selection: warehouse settings
  if (!selected) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom fontWeight={600}>{t('components.mapPropertiesPanel.warehouseSettings')}</Typography>
        <Divider sx={{ mb: 2 }} />
        <WarehouseSizeInputs
          width={warehouseWidth}
          height={warehouseHeight}
          onChange={onWarehouseSizeChange}
          widthLabel={t('components.mapPropertiesPanel.widthGrid')}
          heightLabel={t('components.mapPropertiesPanel.heightGrid')}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: occupancy ? 2 : 0 }}>
          {t('components.mapPropertiesPanel.gridMeterNote')}
        </Typography>

        {occupancy && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
              {t('components.mapPropertiesPanel.occupancyTitle')}
            </Typography>
            <Typography variant="h5" fontWeight={700} color="primary.main" sx={{ lineHeight: 1.2, mb: 0.5 }}>
              {t('pages.dashboard.summaryCards.percentValue', { value: occupancy.occupancyRatePercent })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('components.mapPropertiesPanel.occupancyDetail', {
                occupied: occupancy.occupiedLocations,
                total: occupancy.totalPickableLocations,
              })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('components.mapPropertiesPanel.occupancyEmpty', { count: occupancy.emptyLocations })}
            </Typography>
          </>
        )}
      </Box>
    );
  }

  // Entry/Exit
  if (selected.kind === 'entryExit') {
    const item = selected.item;
    // Faz 1: Entry/Exit görseli 2x2 grid alır → depo bounds içinde clamp
    const warehouseBounds: BoundsRect = { x: 0, y: 0, width: warehouseWidth, height: warehouseHeight };
    const eeMaxX = Math.max(0, warehouseWidth - ENTRY_EXIT_GRID_SIZE);
    const eeMaxY = Math.max(0, warehouseHeight - ENTRY_EXIT_GRID_SIZE);
    const handleEEPosChange = (axis: 'x' | 'y', raw: number) => {
      const proposedX = axis === 'x' ? raw : item.gridX;
      const proposedY = axis === 'y' ? raw : item.gridY;
      const { x, y } = clampPositionToBounds(
        proposedX,
        proposedY,
        ENTRY_EXIT_GRID_SIZE,
        ENTRY_EXIT_GRID_SIZE,
        warehouseBounds,
      );
      onEntryExitChange(item.id, { gridX: x, gridY: y });
    };
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Chip label={item.nodeType === 'Entry' ? t('components.mapPropertiesPanel.entry') : t('components.mapPropertiesPanel.exit')} size="small"
            color={item.nodeType === 'Entry' ? 'success' : 'warning'} variant="outlined" />
          <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flex: 1 }}>
            {item.name || (item.nodeType === 'Entry' ? t('components.mapPropertiesPanel.entryPoint') : t('components.mapPropertiesPanel.exitPoint'))}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
          <InputLabel>{t('components.locationDetailForm.type')}</InputLabel>
          <Select
            value={item.nodeType}
            label={t('components.locationDetailForm.type')}
            onChange={e => onEntryExitChange(item.id, { nodeType: e.target.value as 'Entry' | 'Exit' })}
          >
            <MenuItem value="Entry">{t('components.mapPropertiesPanel.entry')}</MenuItem>
            <MenuItem value="Exit">{t('components.mapPropertiesPanel.exit')}</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label={t('components.locationDetailForm.name')}
          size="small"
          fullWidth
          value={item.name}
          onChange={e => onEntryExitChange(item.id, { name: e.target.value })}
          sx={{ mb: 1.5 }}
        />
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField label="X" type="number" size="small" value={item.gridX}
            onChange={e => handleEEPosChange('x', parseInt(e.target.value) || 0)}
            inputProps={{ min: 0, max: eeMaxX }} />
          <TextField label="Y" type="number" size="small" value={item.gridY}
            onChange={e => handleEEPosChange('y', parseInt(e.target.value) || 0)}
            inputProps={{ min: 0, max: eeMaxY }} />
        </Box>
        <Divider sx={{ my: 1.5 }} />
        {onDuplicate && (
          <Button variant="outlined" color="primary" size="small" startIcon={<ContentCopyIcon />}
            onClick={onDuplicate} fullWidth sx={{ mb: 1 }}>
            {t('components.mapPropertiesPanel.duplicate')}
          </Button>
        )}
        <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />}
          onClick={() => onEntryExitDelete(item.id)} fullWidth>
          {t('components.mapPropertiesPanel.remove')}
        </Button>
      </Box>
    );
  }

  // Obstacle
  if (selected.kind === 'obstacle') {
    const item = selected.item;
    // Faz 1: Engel depo bounds içinde clamp
    const warehouseBounds: BoundsRect = { x: 0, y: 0, width: warehouseWidth, height: warehouseHeight };
    const obsMaxX = Math.max(0, warehouseWidth - item.gridWidth);
    const obsMaxY = Math.max(0, warehouseHeight - item.gridHeight);
    const obsMaxW = Math.max(1, warehouseWidth - item.gridX);
    const obsMaxH = Math.max(1, warehouseHeight - item.gridY);
    const handleObsPosChange = (axis: 'x' | 'y', raw: number) => {
      const proposedX = axis === 'x' ? raw : item.gridX;
      const proposedY = axis === 'y' ? raw : item.gridY;
      const { x, y } = clampPositionToBounds(proposedX, proposedY, item.gridWidth, item.gridHeight, warehouseBounds);
      onObstacleChange(item.id, { gridX: x, gridY: y });
    };
    const handleObsSizeChange = (axis: 'w' | 'h', raw: number) => {
      const proposedW = axis === 'w' ? raw : item.gridWidth;
      const proposedH = axis === 'h' ? raw : item.gridHeight;
      const { width, height } = clampSizeToBounds(item.gridX, item.gridY, proposedW, proposedH, warehouseBounds);
      onObstacleChange(item.id, { gridWidth: width, gridHeight: height });
    };
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Chip label={t('components.mapPropertiesPanel.obstacle')} size="small" color="default" variant="outlined" />
          <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flex: 1 }}>
            {TYPE_LABELS[item.obstacleType] || t('components.mapPropertiesPanel.obstacle')}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
          <InputLabel>{t('components.mapPropertiesPanel.obstacleType')}</InputLabel>
          <Select value={item.obstacleType} label={t('components.mapPropertiesPanel.obstacleType')}
            onChange={e => onObstacleChange(item.id, { obstacleType: e.target.value as ObstacleItem['obstacleType'] })}>
            <MenuItem value="Wall">{t('components.mapPropertiesPanel.wall')}</MenuItem>
            <MenuItem value="Column">{t('components.mapPropertiesPanel.column')}</MenuItem>
            <MenuItem value="FireExit">{t('components.mapPropertiesPanel.fireExit')}</MenuItem>
            <MenuItem value="Other">{t('components.mapPropertiesPanel.other')}</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField label="X" type="number" size="small" value={item.gridX}
            onChange={e => handleObsPosChange('x', parseInt(e.target.value) || 0)}
            inputProps={{ min: 0, max: obsMaxX }} />
          <TextField label="Y" type="number" size="small" value={item.gridY}
            onChange={e => handleObsPosChange('y', parseInt(e.target.value) || 0)}
            inputProps={{ min: 0, max: obsMaxY }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField label={t('components.mapPropertiesPanel.width')} type="number" size="small" value={item.gridWidth}
            onChange={e => handleObsSizeChange('w', Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1, max: obsMaxW }} />
          <TextField label={t('components.mapPropertiesPanel.height')} type="number" size="small" value={item.gridHeight}
            onChange={e => handleObsSizeChange('h', Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1, max: obsMaxH }} />
        </Box>
        <Divider sx={{ my: 1.5 }} />
        {onDuplicate && (
          <Button variant="outlined" color="primary" size="small" startIcon={<ContentCopyIcon />}
            onClick={onDuplicate} fullWidth sx={{ mb: 1 }}>
            {t('components.mapPropertiesPanel.duplicate')}
          </Button>
        )}
        <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />}
          onClick={() => onObstacleDelete(item.id)} fullWidth>
          {t('components.mapPropertiesPanel.remove')}
        </Button>
      </Box>
    );
  }

  // Location (Zone, Aisle, Platform, Rack)
  const loc = selected.item;
  const isPlatform = loc.type === 'Platform';
  const isRack = loc.type === 'Rack';
  const isAisle = loc.type === 'Aisle';

  const parentAisle = isPlatform && loc.aisleId
    ? locations.find(l => l.id === loc.aisleId && !l.isDeleted)
    : null;
  const parentPlatform = isRack && loc.parentId
    ? locations.find(l => l.id === loc.parentId && l.type === 'Platform' && !l.isDeleted)
    : null;
  const parentColumnRack = isRack && loc.parentId
    ? locations.find(l => l.id === loc.parentId && l.type === 'Rack' && !l.isDeleted)
    : null;

  const isColumnRack = isRack && !parentColumnRack;
  const isShelfLevel = isRack && !!parentColumnRack;

  const childCount = (isPlatform || isColumnRack)
    ? locations.filter(l => l.parentId === loc.id && !l.isDeleted).length
    : 0;
  const shelfLevels = isColumnRack
    ? locations.filter(l => l.parentId === loc.id && l.type === 'Rack' && !l.isDeleted)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.name.localeCompare(b.name))
    : [];

  // Faz 1: Tipine göre bounds çözümle (drag/resize ile aynı kuralı manuel input'a uygula)
  const warehouseBounds: BoundsRect = { x: 0, y: 0, width: warehouseWidth, height: warehouseHeight };
  let locBounds: BoundsRect = warehouseBounds;
  if (isAisle) {
    const parentZone = findParentLocation(loc, locations);
    if (parentZone && parentZone.type === 'Zone') locBounds = rectFromLocation(parentZone);
  } else if (isPlatform && parentAisle) {
    locBounds = platformBoundsForAisle(parentAisle, loc.gridWidth, loc.gridHeight, loc.aisleSide);
  } else if (isRack && parentPlatform) {
    locBounds = rectFromLocation(parentPlatform);
  }

  const locMaxX = Math.max(locBounds.x, locBounds.x + locBounds.width - loc.gridWidth);
  const locMaxY = Math.max(locBounds.y, locBounds.y + locBounds.height - loc.gridHeight);
  const locMaxW = Math.max(1, locBounds.x + locBounds.width - loc.gridX);
  const locMaxH = Math.max(1, locBounds.y + locBounds.height - loc.gridY);

  const handleLocPosChange = (axis: 'x' | 'y', raw: number) => {
    const proposedX = axis === 'x' ? raw : loc.gridX;
    const proposedY = axis === 'y' ? raw : loc.gridY;
    const { x, y } = clampPositionToBounds(proposedX, proposedY, loc.gridWidth, loc.gridHeight, locBounds);
    onLocationChange(loc.id, { gridX: x, gridY: y });
  };
  const handleLocSizeChange = (axis: 'w' | 'h', raw: number) => {
    const proposedW = axis === 'w' ? Math.max(1, raw) : loc.gridWidth;
    const proposedH = axis === 'h' ? Math.max(1, raw) : loc.gridHeight;
    const { width, height } = clampSizeToBounds(loc.gridX, loc.gridY, proposedW, proposedH, locBounds);
    onLocationChange(loc.id, { gridWidth: width, gridHeight: height });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip label={TYPE_LABELS[loc.type] || loc.type} size="small" color="primary" variant="outlined" />
        <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flex: 1 }}>{loc.name}</Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <TextField label={t('components.locationDetailForm.name')} size="small" fullWidth value={loc.name}
        onChange={e => onLocationChange(loc.id, { name: e.target.value })}
        sx={{ mb: 1.5 }} />
      <TextField label={t('components.locationDetailForm.code')} size="small" fullWidth value={loc.code}
        onChange={e => onLocationChange(loc.id, { code: e.target.value })}
        sx={{ mb: 1.5 }} />

      {/* Platform: linked aisle info (read-only) */}
      {isPlatform && parentAisle && (
        <TextField label={t('components.mapPropertiesPanel.linkedAisle')} size="small" fullWidth value={parentAisle.name}
          InputProps={{ readOnly: true }} sx={{ mb: 1.5 }} />
      )}
      {(isPlatform || isRack) && (
        <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
          <InputLabel>{t('components.locationDetailForm.side')}</InputLabel>
          <Select
            value={loc.aisleSide || ''}
            label={t('components.locationDetailForm.side')}
            onChange={e => {
              const val = e.target.value as AisleSide | '';
              onLocationChange(loc.id, { aisleSide: val || null });
            }}
          >
            <MenuItem value="Left">{t('components.locationDetailForm.sideLeft')}</MenuItem>
            <MenuItem value="Right">{t('components.locationDetailForm.sideRight')}</MenuItem>
            <MenuItem value="Top">{t('components.locationDetailForm.sideTop', { defaultValue: 'Üst' })}</MenuItem>
            <MenuItem value="Bottom">{t('components.locationDetailForm.sideBottom', { defaultValue: 'Alt' })}</MenuItem>
          </Select>
        </FormControl>
      )}

      {/* Kolon raf: üst üste dikey seviyeler */}
      {isColumnRack && shelfLevels.length > 0 && (() => {
        const occupiedCount = shelfLevels.filter(r => occupiedLocationIds?.has(r.id)).length;
        return (
        <Box sx={{ mb: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
            Üst üste {shelfLevels.length} raf seviyesi{occupiedCount > 0 ? ` (${occupiedCount} dolu)` : ''}:
          </Typography>
          {shelfLevels.map(r => {
            const isOccupied = !!occupiedLocationIds?.has(r.id);
            return (
            <Typography
              key={r.id}
              variant="caption"
              sx={{
                display: 'block',
                fontFamily: 'monospace',
                // Ürünü olan rafı belirgin yap (ağaç görünümüyle aynı stil).
                color: isOccupied ? '#bf360c' : 'text.secondary',
                fontWeight: isOccupied ? 700 : 400,
                bgcolor: isOccupied ? 'rgba(230, 81, 0, 0.14)' : undefined,
                borderLeft: isOccupied ? '4px solid #212121' : '4px solid transparent',
                borderRadius: 0.5,
                pl: 0.5,
                py: '1px',
              }}
            >
              ↳ {r.name} ({r.code}){r.isPickable ? ' • toplanabilir' : ''}{isOccupied ? ' • ürün var' : ''}
            </Typography>
            );
          })}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Detay için Ağaç Görünümünde genişletin.
          </Typography>
        </Box>
        );
      })()}

      {isShelfLevel && parentColumnRack && (
        <TextField label="Kolon (Peron)" size="small" fullWidth value={parentColumnRack.name}
          InputProps={{ readOnly: true }} sx={{ mb: 1.5 }} />
      )}

      {/* Rack: linked platform info */}
      {isRack && parentPlatform && (
        <TextField label={t('components.mapPropertiesPanel.linkedPlatform')} size="small" fullWidth value={parentPlatform.name}
          InputProps={{ readOnly: true }} sx={{ mb: 1.5 }} />
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <TextField label="X" type="number" size="small" value={loc.gridX}
          onChange={e => handleLocPosChange('x', parseInt(e.target.value) || 0)}
          inputProps={{ min: locBounds.x, max: locMaxX }} />
        <TextField label="Y" type="number" size="small" value={loc.gridY}
          onChange={e => handleLocPosChange('y', parseInt(e.target.value) || 0)}
          inputProps={{ min: locBounds.y, max: locMaxY }} />
      </Box>

      {!isRack && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField label={t('components.mapPropertiesPanel.width')} type="number" size="small" value={loc.gridWidth}
            onChange={e => handleLocSizeChange('w', parseInt(e.target.value) || 1)}
            inputProps={{ min: 1, max: locMaxW }} />
          <TextField label={t('components.mapPropertiesPanel.height')} type="number" size="small" value={loc.gridHeight}
            onChange={e => handleLocSizeChange('h', parseInt(e.target.value) || 1)}
            inputProps={{ min: 1, max: locMaxH }} />
        </Box>
      )}

      {isPlatform && childCount > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {t('components.mapPropertiesPanel.platformCount', { count: childCount })}
        </Typography>
      )}

      <Divider sx={{ my: 1.5 }} />

      {onDuplicate && (
        <Button variant="outlined" color="primary" size="small" startIcon={<ContentCopyIcon />}
          onClick={onDuplicate} fullWidth sx={{ mb: 1 }}>
          {t('components.mapPropertiesPanel.duplicate')}
        </Button>
      )}
      <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />}
        onClick={() => onLocationDelete(loc.id)} fullWidth>
        {t('components.mapPropertiesPanel.deleteByType', { type: TYPE_LABELS[loc.type] || loc.type })}
      </Button>
    </Box>
  );
};

/**
 * Depo grid genişlik/yükseklik input'ları.
 * Lokal string state ile kullanıcı klavyeden rahatça yazar (her tuşta clamp olmaz).
 * Commit anı: input blur veya Enter. Dış değişiklikler (undo, vs.) lokal state'e senkron yansır.
 */
const WarehouseSizeInputs: React.FC<{
  width: number;
  height: number;
  onChange: (w: number, h: number) => void;
  widthLabel: string;
  heightLabel: string;
}> = ({ width, height, onChange, widthLabel, heightLabel }) => {
  const [wStr, setWStr] = useState(String(width));
  const [hStr, setHStr] = useState(String(height));

  // Dışarıdan gelen değişiklikleri yansıt (undo, ilk yükleme, vs.).
  useEffect(() => { setWStr(String(width)); }, [width]);
  useEffect(() => { setHStr(String(height)); }, [height]);

  const clamp = (n: number) => Math.min(200, Math.max(10, isFinite(n) ? n : 10));

  const commitW = () => {
    const n = clamp(parseInt(wStr, 10));
    setWStr(String(n));
    if (n !== width) onChange(n, height);
  };
  const commitH = () => {
    const n = clamp(parseInt(hStr, 10));
    setHStr(String(n));
    if (n !== height) onChange(width, n);
  };

  return (
    <>
      <TextField
        label={widthLabel}
        type="number"
        size="small"
        fullWidth
        value={wStr}
        onChange={e => setWStr(e.target.value)}
        onBlur={commitW}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        inputProps={{ min: 10, max: 200 }}
        sx={{ mb: 1.5 }}
      />
      <TextField
        label={heightLabel}
        type="number"
        size="small"
        fullWidth
        value={hStr}
        onChange={e => setHStr(e.target.value)}
        onBlur={commitH}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        inputProps={{ min: 10, max: 200 }}
        sx={{ mb: 1.5 }}
      />
    </>
  );
};

export default MapPropertiesPanel;
